// src/components/Sparkline.jsx
//
// Small hand-rolled inline-SVG line sparkline — the dashboard's one
// convention for a single-series trend readout, mirroring
// StreamActivityMonitor.jsx's bar-sparkline variant elsewhere in the app.
// No charting library exists in this repo (verified via package.json); this
// was originally a local function inside TrendsPage.jsx (WP5,
// docs/har-corpus-spec.md §8) and is extracted here so a second caller
// (SpendIndicator.jsx, docs/llm-spend-visibility-spec.md) can reuse it
// instead of hand-rolling a third sparkline.
//
// Extraction is byte-for-byte behavior-preserving for TrendsPage's two call
// sites: `points`/`baselineValue`/`width`/`height` are unchanged, and the
// two additions below (`ariaLabel`, per-point `label`) are both opt-in —
// TrendsPage never passes them, so its rendered output (and the default
// role="img" aria-label="trend sparkline") is identical to before.
//
//   points        [{ value, gated?, isCurrent?, label? }] — `label`, when
//                 present, renders as a native SVG <title> on that point's
//                 circle (hover tooltip); omit it for no tooltip.
//   baselineValue a threshold rendered as a recessive dashed reference line
//                 (e.g. a cohort baseline in TrendsPage, or a daily spend
//                 cap in SpendIndicator) — never a second prominent series.
//   ariaLabel     overrides the default role="img" accessible label.

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

export default function Sparkline({ points, baselineValue, width = 140, height = 34, ariaLabel = 'trend sparkline' }) {
  const withVals = (points || []).filter((p) => typeof p.value === 'number' && Number.isFinite(p.value));
  if (!withVals.length) {
    return (
      <Box sx={{ width, height, display: 'flex', alignItems: 'center' }}>
        <Typography variant="caption" color="text.disabled">no data</Typography>
      </Box>
    );
  }
  const vals = withVals.map((p) => p.value);
  const lo = Math.min(...vals, typeof baselineValue === 'number' ? baselineValue : Infinity);
  const hi = Math.max(...vals, typeof baselineValue === 'number' ? baselineValue : -Infinity);
  const range = hi - lo || 1;
  const n = points.length;
  const x = (i) => (n <= 1 ? width / 2 : (i / (n - 1)) * width);
  const y = (v) => height - ((v - lo) / range) * (height - 6) - 3;

  const coords = points.map((p, i) => ({
    ...p,
    cx: x(i),
    cy: typeof p.value === 'number' && Number.isFinite(p.value) ? y(p.value) : null,
  }));
  const linePts = coords.filter((c) => c.cy != null).map((c) => `${c.cx},${c.cy}`).join(' ');
  const baseY = typeof baselineValue === 'number' ? y(baselineValue) : null;

  return (
    <svg width={width} height={height} role="img" aria-label={ariaLabel}>
      {baseY != null && (
        <line x1={0} x2={width} y1={baseY} y2={baseY} stroke="currentColor" strokeOpacity="0.25" strokeDasharray="3,3" />
      )}
      {linePts && <polyline points={linePts} fill="none" stroke="currentColor" strokeOpacity="0.55" strokeWidth="1.5" />}
      {coords.map((c, i) => {
        if (c.cy == null) return null;
        const r = c.isCurrent ? 4 : 2.5;
        return (
          <circle
            key={i}
            cx={c.cx}
            cy={c.cy}
            r={r}
            fill={c.gated ? 'none' : (c.isCurrent ? '#1976d2' : 'currentColor')}
            stroke={c.gated ? 'currentColor' : (c.isCurrent ? '#1976d2' : 'none')}
            strokeWidth={c.gated ? 1.25 : 0}
            fillOpacity={c.gated ? 0 : (c.isCurrent ? 1 : 0.8)}
            strokeOpacity={c.gated ? 0.6 : 1}
          >
            {c.label && <title>{c.label}</title>}
          </circle>
        );
      })}
    </svg>
  );
}
